/**
 * ScenarioManagerPage.tsx — Library of reservoir scenarios (built-in presets,
 *                            benchmark sites, user-saved custom scenarios).
 *                            Load any with one click; adjust on TrainSummaryPage;
 *                            save back as new custom scenario.  JSON import /
 *                            export for sharing between users.
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  FolderOpen, ArrowLeft, Download, Upload, Trash2, Plus, ChevronRight,
  CheckCircle2, BookOpen, Pencil, Sparkles, Database, Save, FileJson,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  loadAllScenarios, loadCustomScenarios, saveCustomScenario,
  deleteCustomScenario, activateScenario, getActiveScenario,
  makeCustomScenario, type SavedScenario, type ScenarioSource,
} from '@/lib/scenarioManager';
import { MODULE_TRAINS } from '@/lib/moduleTrains';

const SOURCE_LABEL: Record<ScenarioSource, string> = {
  preset: 'Preset',
  benchmark: 'Benchmark site',
  perfect: 'Perfect Scenario',
  custom: 'Your custom',
};

const SOURCE_COLOUR: Record<ScenarioSource, string> = {
  preset:    'border-slate-700 bg-slate-900 text-slate-300',
  benchmark: 'border-sky-700 bg-sky-900/30 text-sky-200',
  perfect:   'border-emerald-700 bg-emerald-900/30 text-emerald-200',
  custom:    'border-fuchsia-700 bg-fuchsia-900/30 text-fuchsia-200',
};

export default function ScenarioManagerPage() {
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(0);
  const [filter, setFilter] = useState<ScenarioSource | 'all'>('all');
  const fileInput = useRef<HTMLInputElement>(null);

  const scenarios = useMemo(() => loadAllScenarios(), [refresh]);
  const filtered = filter === 'all' ? scenarios : scenarios.filter(s => s.source === filter);
  const active = getActiveScenario();

  const activate = (s: SavedScenario) => {
    activateScenario(s);
    const trainId = s.trainId ?? 'shallow_uk_eutrophic';
    navigate(`/train-summary/${trainId}`);
  };

  const remove = (s: SavedScenario) => {
    if (s.source !== 'custom') return;
    if (!confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    deleteCustomScenario(s.id);
    setRefresh(r => r + 1);
  };

  const exportOne = (s: SavedScenario) => {
    const blob = new Blob([JSON.stringify(s, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scenario-${s.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAllCustom = () => {
    const all = loadCustomScenarios();
    const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rb-scenarios-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJSON = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target?.result as string);
        const list: SavedScenario[] = Array.isArray(data) ? data : [data];
        for (const s of list) {
          if (!s.inputs || !s.name) continue;
          // Force imported scenarios to be 'custom' and give them a fresh id
          const imported: SavedScenario = {
            ...s,
            id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            source: 'custom',
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          saveCustomScenario(imported);
        }
        setRefresh(r => r + 1);
      } catch (err) {
        alert('Could not parse JSON: ' + (err as Error).message);
      }
    };
    reader.readAsText(file);
  };

  const createBlank = () => {
    const name = prompt('Name for new scenario?', `Custom ${new Date().toLocaleDateString()}`);
    if (!name) return;
    const base = scenarios.find(s => s.id === 'preset_barcombe_default') ?? scenarios[0];
    const s = makeCustomScenario(name, base.inputs, { trainId: base.trainId });
    saveCustomScenario(s);
    setRefresh(r => r + 1);
    activate(s);
  };

  const counts = {
    all: scenarios.length,
    preset: scenarios.filter(s => s.source === 'preset').length,
    benchmark: scenarios.filter(s => s.source === 'benchmark').length,
    custom: scenarios.filter(s => s.source === 'custom').length,
    perfect: 0,
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={FolderOpen} title="Scenario Manager"
        subtitle="Load any reservoir scenario, adjust it on the simulator, save & share"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={createBlank}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white">
              <Plus className="w-4 h-4" /> New from current
            </button>
            <button onClick={() => fileInput.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-sm font-semibold text-sky-200 hover:bg-sky-900/50">
              <Upload className="w-4 h-4" /> Import JSON
            </button>
            <input ref={fileInput} type="file" accept="application/json" className="hidden"
              onChange={e => e.target.files && importJSON(e.target.files[0])} />
            <button onClick={exportAllCustom} disabled={counts.custom === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-fuchsia-700 bg-fuchsia-900/30 px-3 py-1.5 text-sm font-semibold text-fuchsia-200 hover:bg-fuchsia-900/50 disabled:opacity-50">
              <Download className="w-4 h-4" /> Export my custom
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Active scenario banner */}
      {active && (
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-3 flex items-center justify-between gap-3">
          <div className="text-sm flex items-center gap-2 text-sky-100">
            <CheckCircle2 className="w-4 h-4 text-sky-300" />
            <span>Active in simulator: <strong>{active.siteName}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/train-summary/shallow_uk_eutrophic`)}
              className="rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
              <ChevronRight className="w-3.5 h-3.5" /> Open Train Summary
            </button>
          </div>
        </div>
      )}

      {/* How-to */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How it works:</strong> three categories of scenarios are always available — built-in
        <span className="text-slate-300"> Presets</span> (Barcombe default + winter / worst-case), four <span className="text-sky-300">Benchmark sites</span>
        (Loch Leven, Müggelsee, Esthwaite, Bautzen), and your own <span className="text-fuchsia-300">Custom scenarios</span> saved to this
        browser's localStorage. Click <em>Load &amp; analyse</em> to push a scenario into the simulator; adjust the sliders on
        Train Summary; save your changes as a new custom scenario.  Export JSON to share with colleagues; Import JSON to
        load shared work.
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {(['all', 'preset', 'benchmark', 'custom'] as const).map(k => (
          <button key={k} onClick={() => setFilter(k)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold border ${
              filter === k ? 'border-sky-500 bg-sky-900/40 text-sky-100'
                           : 'border-slate-700 bg-slate-900 text-slate-400 hover:bg-slate-800'
            }`}>
            {k === 'all' ? 'All' : SOURCE_LABEL[k as ScenarioSource]} ({k === 'all' ? counts.all : counts[k]})
          </button>
        ))}
      </div>

      {/* Scenario grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(s => (
          <ScenarioCard key={s.id} s={s}
            isActive={active?.scenarioId === s.id || active?.siteId === s.sourceSiteId}
            onActivate={() => activate(s)}
            onExport={() => exportOne(s)}
            onDelete={() => remove(s)}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-6 text-center text-sm text-slate-400">
          No scenarios in this category. Use <strong>New from current</strong> above to create one.
        </div>
      )}

      {/* Quick links */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400">
        <div className="font-semibold text-slate-200 mb-2">Related pages</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate('/benchmark-sites')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-xs text-sky-200 hover:bg-sky-900/50">
            <Database className="w-3.5 h-3.5" /> Benchmark sites & bathymetry
          </button>
          <button onClick={() => navigate('/perfect-scenario')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-700 bg-emerald-900/30 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-900/50">
            <Sparkles className="w-3.5 h-3.5" /> Perfect Scenario
          </button>
          <button onClick={() => navigate('/aeration-knowledge')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700">
            <BookOpen className="w-3.5 h-3.5" /> Aeration Knowledge Base
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function ScenarioCard({ s, isActive, onActivate, onExport, onDelete }: {
  s: SavedScenario;
  isActive: boolean;
  onActivate: () => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const train = MODULE_TRAINS.find(t => t.id === s.trainId);
  return (
    <div className={`rounded-2xl border-2 p-4 ${isActive ? 'border-emerald-700 bg-emerald-900/10' : 'border-slate-700 bg-slate-900'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${SOURCE_COLOUR[s.source]}`}>
            {SOURCE_LABEL[s.source]}
          </span>
          <h3 className="text-sm font-bold text-slate-100 mt-1.5">{s.name}</h3>
          {s.description && (
            <p className="text-[11px] text-slate-400 mt-1 leading-snug line-clamp-3">{s.description}</p>
          )}
          {isActive && (
            <span className="inline-flex items-center gap-1 mt-2 rounded-full bg-emerald-900/40 text-emerald-200 text-[10px] font-semibold px-2 py-0.5">
              <CheckCircle2 className="w-2.5 h-2.5" /> ACTIVE
            </span>
          )}
        </div>
      </div>

      {/* Key params */}
      <div className="mt-3 grid grid-cols-2 gap-1 text-[10px] font-mono">
        <Stat k="Area" v={`${(s.inputs.area_m2 / 1e4).toFixed(1)} ha`} />
        <Stat k="Depth" v={`${s.inputs.depth_mean_m} / ${s.inputs.depth_max_m} m`} />
        <Stat k="Chl-a" v={`${s.inputs.chl_ugL} µg/L`} />
        <Stat k="TP" v={`${(s.inputs.tp_mgL * 1000).toFixed(0)} µg/L`} />
        <Stat k="Strat" v={s.inputs.stratified ? 'yes' : 'polymictic'} />
        <Stat k="Cyano" v={`${(s.inputs.fCyano * 100).toFixed(0)} %`} />
      </div>

      {/* Train hint */}
      {train && (
        <div className="mt-2 text-[10px] text-slate-500">
          Suggested train: <span className="text-sky-300">{train.label}</span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-1.5">
        <button onClick={onActivate}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white">
          <Save className="w-3.5 h-3.5" /> Load &amp; analyse
        </button>
        <button onClick={onExport}
          className="rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-slate-300 hover:bg-slate-700"
          title="Export JSON">
          <FileJson className="w-3.5 h-3.5" />
        </button>
        {s.source === 'custom' && (
          <button onClick={onDelete}
            className="rounded-lg border border-red-800 bg-red-900/30 px-2 py-1.5 text-red-300 hover:bg-red-900/60"
            title="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between items-baseline border-b border-slate-800/50 py-0.5">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-200">{v}</span>
    </div>
  );
}

// Pencil import to keep the bundler happy in case we add edit-in-place later
export const _editIcon = Pencil;
